import { useMemo } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AppAutocomplete, AppTextField } from "../form";
import {
  CUSTOM_FORMAT_PRESET_ID,
  formatOptionGroups,
  formatToOptionKey,
  optionKeyToFormat,
  type ColumnFormat,
  type ColumnFormatType,
} from "../../utils/columnFormat";
import { TABLE_CELL_INPUT_SX, tableCellFieldProps } from "../TableColumnsEditor/tableCellStyles";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

type Props = {
  value?: ColumnFormat;
  onChange: (next: ColumnFormat | undefined) => void;
  disabled?: boolean;
};

type FormatOption = { key: string; label: string; group: string; indented?: boolean };

export function formatOptionLabel(
  t: (key: string, opts?: Record<string, unknown>) => string,
  key: string
): string {
  if (key === "none") return t("columnFormat.type.none");
  if (key.startsWith("custom:")) {
    const type = key.slice("custom:".length) as ColumnFormatType;
    return t("columnFormat.customSummary", { type: t(`columnFormat.type.${type}`) });
  }
  const [type, preset] = key.split(":");
  return `${t(`columnFormat.type.${type}`)} · ${t(`columnFormat.preset.${preset}`)}`;
}

function buildFormatOptions(t: (key: string) => string): FormatOption[] {
  return formatOptionGroups().flatMap((group) => {
    const groupLabel = t(group.groupKey);
    if (group.type === "none") {
      return [{ key: "none", label: t("columnFormat.type.none"), group: groupLabel }];
    }
    if (group.type === "custom") {
      return group.presetIds.map((type) => ({
        key: `custom:${type}`,
        label: t(`columnFormat.customType.${type}`),
        group: groupLabel,
        indented: true,
      }));
    }
    return group.presetIds.map((presetId) => ({
      key: `${group.type}:${presetId}`,
      label: t(`columnFormat.preset.${presetId}`),
      group: groupLabel,
      indented: true,
    }));
  });
}

export function ColumnFormatSelectControl({
  value,
  onChange,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const styles = custom.components.columnFormatSelect;
  const optionKey = formatToOptionKey(value);

  const options = useMemo(() => buildFormatOptions(t), [t]);
  const selected =
    options.find((o) => o.key === optionKey) ??
    options.find((o) => o.key === "none") ??
    null;

  return (
    <AppAutocomplete<FormatOption>
      label={t("columnFormat.typeLabel")}
      options={options}
      value={selected}
      disabled={disabled}
      onChange={(_, opt) => onChange(optionKeyToFormat(opt?.key ?? "none", value))}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.key === b.key}
      groupBy={(o) => o.group}
      disableClearable
      size="small"
      fullWidth
      renderInput={(params) => (
        <AppTextField
          {...params}
          hiddenLabel
          label={t("columnFormat.typeLabel")}
          margin="none"
          sx={TABLE_CELL_INPUT_SX}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <Box
            component="li"
            key={key}
            {...rest}
            sx={option.indented ? styles.menuItemIndented : undefined}
          >
            {option.label}
          </Box>
        );
      }}
      slotProps={{
        paper: { sx: styles.menuPaper },
      }}
      sx={TABLE_CELL_INPUT_SX}
    />
  );
}

export function ColumnFormatCustomFields({ value, onChange, disabled }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();
  const fieldStyles = custom.components.columnFormatSelect;
  const isCustom = value?.preset === CUSTOM_FORMAT_PRESET_ID;
  const activeType = value?.type;

  if (!isCustom || !activeType) {
    return null;
  }

  return (
    <>
      <AppTextField
        {...tableCellFieldProps}
        label={activeType === "date" ? t("columnFormat.patternDate") : t("columnFormat.patternNumber")}
        placeholder={
          activeType === "date"
            ? t("columnFormat.patternDatePlaceholder")
            : t("columnFormat.patternNumberPlaceholder")
        }
        value={value?.pattern || ""}
        onChange={(e) =>
          onChange({
            type: activeType,
            preset: CUSTOM_FORMAT_PRESET_ID,
            pattern: e.target.value,
            ...(activeType === "currency" ? { currency: value?.currency || "TRY" } : {}),
          })
        }
        disabled={disabled}
        sx={fieldStyles.customPatternField}
      />
      {activeType === "currency" && (
        <AppTextField
          {...tableCellFieldProps}
          label={t("columnFormat.currencyCode")}
          placeholder={t("columnFormat.currencyCode")}
          value={value?.currency || "TRY"}
          onChange={(e) =>
            onChange({
              type: activeType,
              preset: CUSTOM_FORMAT_PRESET_ID,
              pattern: value?.pattern || "#,##0.00",
              currency: e.target.value.toUpperCase(),
            })
          }
          disabled={disabled}
          sx={fieldStyles.currencyField}
        />
      )}
    </>
  );
}

/** @deprecated Use ColumnFormatSelectControl + ColumnFormatCustomFields in table layout. */
export default function ColumnFormatSelect(props: Props) {
  return <ColumnFormatSelectControl {...props} />;
}
